/**
 * Error Boundary Component
 *
 * React 에러 바운더리 - 컴포넌트 트리에서 발생한 JavaScript 에러를 포착
 * - 에러 발생 시 폴백 UI 표시
 * - 에러 로깅 (프로덕션 환경에서는 외부 서비스로 전송 가능)
 * - 사용자에게 친화적인 에러 메시지 제공
 *
 * @example
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <YourComponent />
 * </ErrorBoundary>
 */

import React, { Component, ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import ErrorCharacter from "@/assets/images/error-character.png"
import i18next from 'i18next';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  isChunkError: boolean;
}

// Chunk load error 감지 함수
const isChunkLoadError = (error: Error): boolean => {
  const message = error.message || '';
  const name = error.name || '';

  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    name === 'ChunkLoadError' ||
    message.includes('Importing a module script failed')
  );
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Chunk load error인 경우 감지
    const chunkError = isChunkLoadError(error);

    // 다음 렌더링에서 폴백 UI를 보여주도록 상태 업데이트
    return {
      hasError: true,
      isChunkError: chunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // 에러 로깅
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Chunk load error인 경우 자동 새로고침 (무한 루프 방지)
    if (isChunkLoadError(error)) {
      const CHUNK_ERROR_KEY = 'chunk_error_reload';
      const lastReload = sessionStorage.getItem(CHUNK_ERROR_KEY);
      const now = Date.now();

      // 마지막 새로고침으로부터 10초 이상 지났으면 새로고침
      if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
        console.log('[ErrorBoundary] Chunk load error detected, reloading page...');
        sessionStorage.setItem(CHUNK_ERROR_KEY, now.toString());
        window.location.reload();
        return;
      } else {
        console.log('[ErrorBoundary] Chunk load error persists after reload');
      }
    }

    // 상태 업데이트
    this.setState({
      error,
      errorInfo,
    });

    // 커스텀 에러 핸들러 호출 (있는 경우)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 프로덕션 환경에서는 외부 에러 추적 서비스로 전송
    // 예: Sentry, LogRocket, etc.
    // if (process.env.NODE_ENV === 'production') {
    //   sendToErrorTrackingService(error, errorInfo);
    // }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleForceReload = () => {
    // sessionStorage 초기화 후 강제 새로고침
    sessionStorage.removeItem('chunk_error_reload');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // 커스텀 폴백 UI가 제공된 경우 (chunk error가 아닐 때만)
      if (this.props.fallback && !this.state.isChunkError) {
        return this.props.fallback;
      }

      // Chunk load error 전용 UI
      if (this.state.isChunkError) {
        return (
          <ErrorContainer>
            <ErrorCard>
              <ErrorIcon>🔄</ErrorIcon>
              <ErrorTitle>앱 업데이트가 필요합니다</ErrorTitle>
              <ErrorMessage>
                새로운 버전이 배포되어 페이지를 새로고침해야 합니다.
                <br />
                아래 버튼을 클릭하여 새로고침 해주세요.
              </ErrorMessage>
              <ActionButtons>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={this.handleForceReload}
                  size="large"
                >
                  새로고침
                </Button>
                <Button
                  icon={<HomeOutlined />}
                  onClick={this.handleGoHome}
                  size="large"
                >
                  홈으로 이동
                </Button>
              </ActionButtons>
            </ErrorCard>
          </ErrorContainer>
        );
      }

      // 기본 폴백 UI
      return (
        <ErrorContainer>
            {/* 에러 상세 정보 표시 (디버그 모드 또는 개발 환경) */}
            {(import.meta.env.VITE_APP_DEBUG === 'true' || import.meta.env.DEV) && this.state.error && (
              <ErrorCard>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorTitle>앗, 문제가 발생했습니다!</ErrorTitle>
              <ErrorMessage>
                예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 홈으로 이동해주세요.
              </ErrorMessage>
              <ErrorDetails>
                <ErrorDetailsTitle>에러 상세 정보 (개발 모드)</ErrorDetailsTitle>
                <ErrorStack>
                  <strong>{this.state.error.toString()}</strong>
                  {this.state.errorInfo && (
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  )}
                </ErrorStack>
              </ErrorDetails>
              <ActionButtons>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
                size="large"
              >
                다시 시도
              </Button>
              <Button
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
                size="large"
              >
                홈으로 이동
              </Button>
            </ActionButtons>
          </ErrorCard>
            )}

          <ProductionErrorCard>
            <CharacterImage src={ErrorCharacter} alt="Error Character" />
            <ProductionErrorContent>
              <OopsTitle>{i18next.t('errorBoundary.oops')}</OopsTitle>
              <ProductionErrorText>
                <p>{i18next.t('errorBoundary.sorryMessage')}</p>
                <p>{i18next.t('errorBoundary.tryAgainMessage')}</p>
              </ProductionErrorText>
              <GoHomeButton onClick={this.handleGoHome}>
                {i18next.t('errorBoundary.goBackHome')}
              </GoHomeButton>
            </ProductionErrorContent>
          </ProductionErrorCard>

        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

// ==========================================
// Styled Components
// ==========================================

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(180deg, #0A1321 0%, #0F231F 100%);

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const ErrorCard = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 48px;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const ErrorTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 16px;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ErrorDetails = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  text-align: left;
  background-color: #f7fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const ErrorDetailsTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
`;

const ErrorStack = styled.div`
  font-size: 12px;
  color: #4a5568;
  max-height: 200px;
  overflow-y: auto;

  strong {
    display: block;
    color: #e53e3e;
    margin-bottom: 8px;
  }

  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
    font-family: 'Courier New', monospace;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ProductionErrorCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 40px 32px;
  border: 1px solid #353535;
  border-radius: 24px;
  width: 100%;
  max-width: 420px;

  @media (max-width: 768px) {
    border: none;
    padding: 40px 0;
  }
`;

const CharacterImage = styled.img`
  width: 190px;
  height: auto;
  object-fit: contain;

  @media (max-width: 768px) {
    width: 148px;
  }
`;

const ProductionErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

const OopsTitle = styled.h1`
  font-size: 42px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.84px;
  margin: 0;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ProductionErrorText = styled.div`
  font-size: 18px;
  color: #e9e9ed;
  line-height: 1.5;
  text-align: center;
  font-family: 'NanumSquareRound', sans-serif;
  font-style: normal;

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const GoHomeButton = styled.button`
  height: 48px;
  padding: 0 40px;
  border: 1px solid #353535;
  border-radius: 12px;
  background: transparent;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;
